import { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardBody,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Button,
  Flex,
} from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import { helpdeskReportService, type ReportFilters } from '@/lib/services/helpdeskReportService'

const PER_PAGE = 25

export default function HistoricalExportPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number | null>(null)
  const [rows, setRows] = useState<Array<Record<string, string>>>([])
  const [columns, setColumns] = useState<Record<string, string>>({})
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const columnKeys = useMemo(() => Object.keys(columns), [columns])

  useEffect(() => {
    setPage(1)
  }, [filters.from, filters.to, filters.client_id])

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getHistoricalPreview(filters, page, PER_PAGE)
      .then((res) => {
        setCount(res.count ?? res.meta?.total ?? 0)
        setRows(res.data ?? [])
        setColumns(res.columns ?? {})
        setLastPage(res.meta?.last_page ?? 1)
      })
      .catch(() => {
        setCount(0)
        setRows([])
        setColumns({})
        setLastPage(1)
      })
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id, page])

  return (
    <Box p={4}>
      <ReportPageHeader
        title="Historical Export"
        subtitle="Preview imported ticket data from the database, then download the full export"
        reportType="historical"
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Card mb={4} maxW="lg">
        <CardBody>
          <Stat mb={2}>
            <StatLabel>Rows matching filters</StatLabel>
            {loading ? <Skeleton height="8" mt={2} /> : <StatNumber>{count?.toLocaleString() ?? 0}</StatNumber>}
          </Stat>
          <Text fontSize="sm" color="gray.600">
            Preview below uses the same ticket data as the CSV download (source column mapping). Finance and
            custom source fields that were not imported will be empty in the export.
          </Text>
        </CardBody>
      </Card>

      <Card>
        <CardBody overflowX="auto">
          <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
            <Text fontWeight="semibold" fontSize="sm">
              Export preview
            </Text>
            {!loading && count !== null && count > 0 && (
              <Text fontSize="xs" color="gray.500">
                Page {page} of {lastPage} · showing {rows.length} of {count.toLocaleString()} rows
              </Text>
            )}
          </Flex>

          {loading ? (
            <Skeleton height="320px" />
          ) : rows.length === 0 ? (
            <Text fontSize="sm" color="gray.500" py={8} textAlign="center">
              No tickets match the current filters.
            </Text>
          ) : (
            <>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    {columnKeys.map((key) => (
                      <Th key={key} whiteSpace="nowrap">
                        {columns[key]}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <Tr key={row.source_ticket_id}>
                      {columnKeys.map((key) => (
                        <Td key={key} maxW="220px" isTruncated title={row[key]}>
                          {row[key] || '—'}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {lastPage > 1 && (
                <HStack justify="center" mt={4} spacing={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    isDisabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Text fontSize="sm" color="gray.600">
                    {page} / {lastPage}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                    isDisabled={page >= lastPage}
                  >
                    Next
                  </Button>
                </HStack>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  )
}
